import React, { useEffect, useMemo, useState } from "react";
import {
  CheckIcon,
  XMarkIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";

const IconButton = ({ onClick, title, className = "", children }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`h-9 w-9 flex items-center justify-center rounded-md border border-gray-200 bg-white shadow-sm hover:bg-gray-50 transition ${className}`}
  >
    {children}
  </button>
);

const InlineInput = ({ type, value, onChange, onKeyDown, min, max, step }) => (
  <input
    autoFocus
    type={type}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    onKeyDown={onKeyDown}
    min={min}
    max={max}
    step={step}
    className="w-full h-12 appearance-none text-primary placeholder:text-muted border border-gray-300 rounded-md px-4 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary shadow-sm bg-white"
  />
);

const InlineSelect = ({ value, onChange, onKeyDown, options }) => (
  <div className="relative w-full">
    <select
      autoFocus
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      className="w-full h-12 appearance-none bg-white text-primary border border-gray-300 rounded-md px-4 pr-10 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary shadow-sm"
    >
      {options?.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
    <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
  </div>
);

const InlineCheckbox = ({ checked, onChange }) => (
  <input
    autoFocus
    type="checkbox"
    checked={!!checked}
    onChange={(e) => onChange(e.target.checked)}
    className="h-5 w-5 rounded border border-gray-300 bg-white shadow-sm accent-[#FFD76A] focus:outline-none focus:ring-2 focus:ring-primary/20"
  />
);

const EditableCell = ({
  type,
  value,
  onChange,
  onSave,
  onCancel,
  onKeyDown,
  options,
  min,
  max,
  step,
}) => (
  <div className="flex items-center gap-2">
    <div className="flex-1 min-w-[140px]">
      {type === "select" ? (
        <InlineSelect
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          options={options}
        />
      ) : type === "checkbox" ? (
        <InlineCheckbox
          checked={value}
          onChange={(v) => {
            onChange(v);
            setTimeout(onSave, 0);
          }}
        />
      ) : (
        <InlineInput
          type={type}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          min={min}
          max={max}
          step={step}
        />
      )}
    </div>

    {type !== "checkbox" && (
      <>
        <IconButton
          onClick={onSave}
          title="Sauvegarder"
          className="text-emerald-600"
        >
          <CheckIcon className="h-5 w-5" />
        </IconButton>
        <IconButton onClick={onCancel} title="Annuler" className="text-red-600">
          <XMarkIcon className="h-5 w-5" />
        </IconButton>
      </>
    )}
  </div>
);

const SelectBox = ({ checked, onChange, title }) => (
  <input
    type="checkbox"
    title={title}
    checked={!!checked}
    onChange={(e) => onChange(e.target.checked)}
    className="h-5 w-5 rounded border border-gray-300 bg-white shadow-sm accent-[#FFD76A] focus:outline-none focus:ring-2 focus:ring-primary/20"
  />
);

export default function DataGrid({
  columns,
  data,
  onCellEdit,
  loading = false,
  emptyMessage = "Aucun élément trouvé",
  rowSelection = false,
  onSelectionChange,
  className = "",
}) {
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);

  const allSelected = useMemo(
    () => data.length > 0 && selectedRows.length === data.length,
    [data.length, selectedRows.length]
  );

  const startEdit = (rowId, columnKey, currentValue) => {
    setEditingCell({ rowId, columnKey });
    setEditValue(currentValue ?? "");
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue("");
  };

  const saveEdit = async (row) => {
    if (!editingCell || !onCellEdit || !row) return;
    const success = await onCellEdit(row.id, editingCell.columnKey, editValue);
    if (success !== false) cancelEdit();
  };

  const handleSelectAll = (checked) => {
    const newSelection = checked ? data.map((row) => row.id) : [];
    setSelectedRows(newSelection);
    onSelectionChange?.(newSelection);
  };

  const handleSelectRow = (rowId, checked) => {
    const newSelection = checked
      ? [...selectedRows, rowId]
      : selectedRows.filter((id) => id !== rowId);
    setSelectedRows(newSelection);
    onSelectionChange?.(newSelection);
  };

  const handleKeyDown = (e) => {
    if (!editingCell) return;
    if (e.key === "Enter")
      saveEdit(data.find((d) => d.id === editingCell.rowId));
    if (e.key === "Escape") cancelEdit();
  };

  useEffect(() => {
    setSelectedRows((prev) =>
      prev.filter((id) => data.some((r) => r.id === id))
    );
  }, [data]);

  const renderDisplayValue = (column, cellValue, row) => {
    if (column.format) return column.format(cellValue, row);
    return cellValue ?? <span className="text-gray-400">—</span>;
  };

  const renderCell = (row, column) => {
    const isEditing =
      editingCell?.rowId === row.id && editingCell?.columnKey === column.key;
    const cellValue = row[column.key];

    if (column.editable && isEditing) {
      return (
        <EditableCell
          type={column.type || "text"}
          value={editValue}
          onChange={setEditValue}
          onSave={() => saveEdit(row)}
          onCancel={cancelEdit}
          onKeyDown={handleKeyDown}
          options={column.options}
          min={column.min}
          max={column.max}
          step={column.step}
        />
      );
    }

    if (column.render) {
      return column.render(row, {
        startEdit: () => startEdit(row.id, column.key, cellValue),
        isEditing,
      });
    }

    if (column.editable) {
      return (
        <button
          type="button"
          onClick={() => startEdit(row.id, column.key, cellValue)}
          className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-50 transition"
        >
          {renderDisplayValue(column, cellValue, row)}
        </button>
      );
    }

    return (
      <div className="px-4 py-4">
        {renderDisplayValue(column, cellValue, row)}
      </div>
    );
  };

  if (loading) {
    return (
      <div
        className={`w-full border border-gray-200 rounded-lg bg-white shadow-md ${className}`}
      >
        <div className="py-10 text-center text-gray-500">Chargement...</div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div
        className={`w-full border border-gray-200 rounded-lg bg-white shadow-md ${className}`}
      >
        <div className="py-10 text-center text-gray-500">{emptyMessage}</div>
      </div>
    );
  }

  return (
    <div
      className={`w-full overflow-x-auto border border-gray-200 rounded-lg bg-white shadow-md ${className}`}
    >
      <table className="min-w-full text-sm">
        <thead>
          <tr className="bg-[#F7F8FA] border-b border-gray-200">
            {rowSelection && (
              <th className="w-12 px-4 py-3">
                <div className="flex items-center justify-center">
                  <SelectBox
                    checked={allSelected}
                    onChange={handleSelectAll}
                    title="Tout sélectionner"
                  />
                </div>
              </th>
            )}
            {columns.map((column) => (
              <th
                key={column.key}
                className={`text-left px-4 py-3 font-semibold text-primary whitespace-nowrap ${
                  column.headerClassName || ""
                }`}
                style={{ width: column.width }}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {data.map((row, idx) => (
            <tr
              key={row.id || idx}
              className={`${
                idx % 2 === 0 ? "bg-white" : "bg-gray-50"
              } border-b border-gray-100 hover:bg-[#FFF9E6]/40 transition`}
            >
              {rowSelection && (
                <td className="w-12 px-4 py-3 align-top">
                  <div className="flex items-center justify-center pt-1">
                    <SelectBox
                      checked={selectedRows.includes(row.id)}
                      onChange={(checked) => handleSelectRow(row.id, checked)}
                      title="Sélectionner"
                    />
                  </div>
                </td>
              )}

              {columns.map((column) => (
                <td
                  key={column.key}
                  className={`align-center ${column.cellClassName || ""}`}
                >
                  {renderCell(row, column)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
